import React, { useEffect, useRef, useState } from 'react';
import 'react-lazy-load-image-component/src/effects/blur.css';
import { Popover } from 'react-tiny-popover';
import ExperienceSummary from '../../components/ExperienceSummary';
import {
  dateToIsoLocal,
  dateToIsoUTC,
  fromDateUTCToDateLocal,
  startOfDayLocal,
} from '../../../../../../../../shared/lib/time';
import { DatePicker } from '../../components/DatePicker';
import { useForcedEventCache } from '../../context/EventCacheContext';
import { StepPropsBase } from '../Reserve';
import { useCartCache } from '../../context/CartCacheContext';
import { ReserveCacheSlot } from '../../../../../../../../shared/types/reserveCache';
import {
  buildCalendarMarkers,
  buildDateOption,
  buildReserveSlotKey,
  buildTicketSelections,
  buildTimeOption,
  checkTicketsSelectedAreAvailable,
  experienceIsRental,
  slotKeyFromReservationDraft,
} from '../../../../../../../../shared/lib/reserve';
import Button from '../../components/Button';
import TwoColumnLayout from '../../components/TwoColumnLayout';
import Alert from '../../components/Alert';
import Icon from '../../components/Icon';
import { evaluateAudienceZone } from '../../../../../../../../shared/lib/audience';
import { groupBy, mapBy } from '../../../../../../../../shared/lib/functional';
import { TimePicker } from '../../components/TimePicker';
import { TicketPicker } from '../../components/TicketPicker';
import DateCalendar from '../../components/DateCalendar';
import {
  DateOption,
  TimeOption,
  TicketSelections,
} from '../../../../../../../../shared/types/reserve';

export interface DateTimeSelections {
  ticketQuantities: Record<string, number>;
  dateIndex: number | null;
  timeIndex: number | null;
}

interface DateTimeViewPropsBase {
  initialDate?: Date | null;
}

type DateTimeViewProps = DateTimeViewPropsBase & StepPropsBase;

export default function DateTimeView(props: DateTimeViewProps) {
  const eventDiv = useRef<HTMLDivElement>(null);

  const { onNext, onBack, initialDate } = props;
  const { event, allEvents } = useForcedEventCache();
  const { saveCart, activeDraft, passiveUpdateSlot } = useCartCache();

  const isRental = experienceIsRental(event.eventTemplate.reservables?.experienceType);
  const isNewSession =
    activeDraft?.reservationDraft.eventSnapshot.eventTemplate !== event.eventTemplate?._id;

  const draft = isNewSession ? undefined : activeDraft?.reservationDraft;
  const draftTemplate = isNewSession ? event.eventTemplate : draft?.eventSnapshot;
  const availableTickets = draftTemplate?.reservables.tickets ?? [];

  const mySlot = draft
    ? event.timeslots.find(t => t.slotKey === slotKeyFromReservationDraft(draft))
    : undefined;
  const myTickets = isNewSession ? [] : (draft?.tickets ?? []);

  const [loading, setLoading] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [calendarSelectedRental, setCalendarSelectedRental] = useState<string | undefined>(
    undefined
  );

  const timeslotsByDate = groupBy(event?.timeslots, t => dateToIsoUTC(t.date));
  const allHosts = mapBy(event?.collections?.hosts ?? [], h => h._id);

  function resetSelectedDate() {
    return buildDateOption(
      dateToIsoLocal(
        selectedSlot ? fromDateUTCToDateLocal(selectedSlot.date) : initialDate || startOfDayLocal()
      ),
      timeslotsByDate
    );
  }

  const [selectedSlot, setSelectedSlot] = useState<ReserveCacheSlot | undefined>(mySlot);
  const [selectedDate, setSelectedDate] = useState<DateOption>(resetSelectedDate());
  const [selectedTime, setSelectedTime] = useState<TimeOption | undefined>(
    selectedSlot ? buildTimeOption(selectedSlot, allHosts) : undefined
  );
  const [ticketSelections, setTicketSelections] = useState<TicketSelections>(
    buildTicketSelections(availableTickets, isNewSession ? undefined : myTickets)
  );

  const allTimesFromSelectedDate = timeslotsByDate[selectedDate.key] ?? [];
  const totalTicketsToPurchase = Object.values(ticketSelections.quantities).reduce(
    (sum, v) => sum + v,
    0
  );

  const ticketReservationLimits = availableTickets.reduce(
    (acc, t) => {
      if (t.limitPerReservation !== undefined) acc[t._id] = t.limitPerReservation;
      return acc;
    },
    {} as Record<string, number>
  );

  // Passive slot preservation: whenever date, time, or ticket selection changes, find the
  // matching slot and update selectedSlot automatically.
  useEffect(() => {
    if (!selectedTime || !selectedDate) {
      setSelectedSlot(undefined);
      return;
    }

    const slotsForDate = timeslotsByDate[selectedDate.key] ?? [];

    const rental = isRental
      ? Object.entries(ticketSelections.quantities).find(([, qty]) => qty > 0)?.[0]
      : undefined;

    const slotKey = buildReserveSlotKey(
      { date: selectedDate.key, rental, host: selectedTime.host },
      selectedTime.time,
      selectedTime.duration
    );

    const match = slotsForDate.find(slot => slot.slotKey === slotKey);

    setSelectedSlot(match);
  }, [selectedDate, selectedTime, ticketSelections]);

  useEffect(() => {
    if (!selectedSlot) return;
    passiveUpdateSlot(selectedSlot);
  }, [selectedSlot]);

  const withinTicketLimits =
    selectedSlot && isNewSession
      ? availableTickets.every(
          t =>
            checkTicketsSelectedAreAvailable(
              t._id,
              selectedSlot,
              ticketSelections.quantities,
              ticketReservationLimits
            ) === undefined
        )
      : true;

  const canContinue = selectedSlot && totalTicketsToPurchase > 0 && withinTicketLimits;

  async function handleOnNext() {
    if (loading || !canContinue || !draftTemplate || !selectedSlot) return;

    setLoading(true);

    try {
      if (isNewSession) {
        // new cart
        await saveCart({
          eventTemplateId: draftTemplate._id,
          slotKey: selectedSlot.slotKey,
          tickets: Object.entries(ticketSelections.quantities)
            .filter(([_, qty]) => qty > 0)
            .map(([ticketId, qty]) => ({
              ticketId,
              amount: qty,
              values: [],
            })),
        });
        onNext?.();
      } else {
        // existing cart
        await saveCart();
        onNext?.();
      }
    } finally {
      setLoading(false);
    }
  }

  const dataCollections = allEvents?.collections ?? {
    segments: [],
    levels: [],
    attributes: [],
    zones: [],
  };

  const visibleZones = dataCollections.zones
    .filter(z => evaluateAudienceZone('visible', z._id, undefined, event.eventTemplate.audience))
    .map(z => (
      <div
        key={z._id}
        className="text-white shrink-0 min-w-0 bg-black text-sm font-bold rounded-full px-2 py-0 truncate go-event-zone"
        style={{ backgroundColor: z.color }}
      >
        {z.name}
      </div>
    ));

  const exploreButton = () => (
    // @ts-ignore
    <Popover
      isOpen={isCalendarOpen}
      positions={['bottom', 'top']}
      boundaryElement={eventDiv.current || undefined}
      parentElement={eventDiv.current || undefined}
      onClickOutside={() => {
        setIsCalendarOpen(false);
      }}
      containerStyle={{ zIndex: '10' }}
      content={
        <DateCalendar
          selectedDate={selectedDate.date}
          onDateSelect={d => {
            setSelectedDate(buildDateOption(dateToIsoLocal(d), timeslotsByDate));
            setIsCalendarOpen(false);
          }}
          addMarkers={dc =>
            buildCalendarMarkers(
              dc.dateIso,
              timeslotsByDate,
              ticketSelections.quantities,
              selectedTime,
              calendarSelectedRental
            )
          }
          onClear={() => {
            setSelectedDate(resetSelectedDate());
            setIsCalendarOpen(false);
          }}
          className="shadow-lg border border-table"
          showClear
          showToday
          subtitle={() => (
            <div className="text-xs text-secondary">
              <div className="flex items-center gap-1">
                <span
                  className="inline-block rounded-full"
                  style={{ width: 8, height: 8, backgroundColor: '#22c55e' }}
                />
                Available for your time slot and party
              </div>
              <div className="flex items-center gap-1">
                <span
                  className="inline-block rounded-full"
                  style={{ width: 8, height: 8, backgroundColor: '#f59e0b' }}
                />
                Options, but do not fit your selections
              </div>
              <div className="flex items-center gap-1">
                <span
                  className="inline-block rounded-full"
                  style={{ width: 8, height: 8, backgroundColor: '#ef4444' }}
                />
                Not available
              </div>
            </div>
          )}
        />
      }
    >
      <div>
        <Button
          onClick={() => {
            setCalendarSelectedRental(undefined);
            setIsCalendarOpen(!isCalendarOpen);
          }}
          icon={['fass', 'calendar']}
          variant="borderlike"
          inverse
          className="text-active-i -mr-2 -mt-2"
          disabled={!isNewSession}
        >
          Explore Dates
        </Button>
      </div>
    </Popover>
  );

  return (
    <TwoColumnLayout
      ref={eventDiv}
      tags={visibleZones}
      title={draftTemplate?.title}
      subtitle="Date & Time"
      prepend={
        !isNewSession ? (
          <Alert>
            <Icon icon={['fass', 'lock']} className="text-warning mr-2" />
            <span className="font-bold">Ticket quantities are locked once reserved.&nbsp;</span>
            <span>
              To make changes, remove the item from your cart and start a new reservation.
            </span>
          </Alert>
        ) : undefined
      }
      aside={
        <ExperienceSummary
          potentialDraft={
            isNewSession && selectedSlot
              ? {
                  slotKey: selectedSlot?.slotKey,
                  ticketQuantities: ticketSelections.quantities,
                }
              : undefined
          }
          onBack={() => onBack?.()}
          onNext={handleOnNext}
          nextDisabled={!canContinue}
          loading={loading}
        />
      }
    >
      <fieldset disabled={!isNewSession} className="grid grid-cols-1 gap-4">
        <div className="rounded-lg border border-border p-6 grid grid-cols-1 gap-8">
          <div className="flex items-center">
            <div>
              <div className="font-bold">Step 1 of 3</div>
              <div className="font-bold text-lg text-accent">Select Date</div>
            </div>
            <div className="ml-auto">{exploreButton()}</div>
          </div>
          <DatePicker
            selectedDate={selectedDate}
            onSelectedDate={setSelectedDate}
            timeslots={timeslotsByDate}
          />
        </div>
        {selectedDate && (
          <div className="rounded-lg border border-border p-6 grid grid-cols-1 gap-8">
            <div>
              <div className="font-bold">Step 2 of 3</div>
              <div className="font-bold text-lg text-accent">Select Time</div>
            </div>
            <TimePicker
              selectedTime={selectedTime}
              onSelectedTime={setSelectedTime}
              timeslots={allTimesFromSelectedDate}
              hosts={allHosts}
            />
          </div>
        )}
        {selectedTime && (
          <div className="rounded-lg border border-border p-6 grid grid-cols-1 gap-8">
            <div>
              <div className="font-bold">Step 3 of 3</div>
              <div className="font-bold text-lg text-accent">
                Select {isRental ? 'Rental' : 'Tickets'}
              </div>
            </div>
            <TicketPicker
              tickets={availableTickets}
              selected={ticketSelections}
              onSelected={setTicketSelections}
              isRental={isRental}
              autoAssignRental={!!draftTemplate?.reservables.autoAssignRental}
              isNewSession={isNewSession}
              onExploreDates={selectedRental => {
                setCalendarSelectedRental(selectedRental);
                setIsCalendarOpen(true);
              }}
              selectedSlot={selectedSlot}
              ticketReservationLimits={ticketReservationLimits}
              timeslots={allTimesFromSelectedDate}
              selectedDate={selectedDate}
              selectedTime={selectedTime}
            />
          </div>
        )}
      </fieldset>
    </TwoColumnLayout>
  );
}
